import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import {
  getProductDisplayName,
  ProductIcon,
  sortProducts,
} from '@app/app-updates-product-app';
import { UiProductType } from '~src/packages/app-updates-product-app/src/types';

const messages = defineMessages({
  appsImpactedTitle: {
    id: 'appUpdatesDetails.sidebar.appsImpactedTitle',
    defaultMessage: 'Apps impacted',
    description: 'Title for the section displaying apps impacted by the update',
  },
});

const titleStyles = xcss({
  marginBlockEnd: 'space.100',
});

const contentStyles = xcss({
  fontWeight: 'font.weight.regular',
  color: 'color.text.subtlest',
  marginBlockStart: 'space.150',
  gap: 'space.100',
});

const productItemStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
});

export interface AppImpactedSidebarProps {
  /**
   * Array of products impacted by the update
   */
  products?: readonly UiProductType[] | null;
}

/**
 * Sidebar component that displays the list of apps impacted by an update.
 * Shows only the app names and icons in a clean, simplified layout.
 *
 * Example:
 * ```tsx
 * <AppImpactedSidebar
 *   products={['JIRA', 'CONFLUENCE', 'BITBUCKET']}
 * />
 * ```
 */
export const AppImpactedSidebar: FunctionComponent<AppImpactedSidebarProps> = ({
  products,
}) => {
  const { formatMessage } = useIntl();

  // Don't render if no products
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Stack>
      <section data-testid="app-impacted-sidebar-products">
        <Box xcss={titleStyles}>
          <Heading as="h3" size="small">
            {formatMessage(messages.appsImpactedTitle)}
          </Heading>
        </Box>
        <Stack xcss={contentStyles}>
          {sortProducts(products).map((productType) => (
            <Box
              key={productType}
              xcss={productItemStyles}
              testId={`app-display-${productType}`}
            >
              <ProductIcon
                productType={productType}
                size="small"
                testId="product-list-primary-icon"
              />
              <Text>{getProductDisplayName(productType)}</Text>
            </Box>
          ))}
        </Stack>
      </section>
    </Stack>
  );
};

export default AppImpactedSidebar;
